import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { formTemplateService, ServiceProviderOption } from '../../../services/formTemplate.service';

interface Props {
  value: string;
  onChange: (option: ServiceProviderOption | null) => void;
  required?: boolean;
}

/**
 * Service-provider dropdown fed by the Dataverse-synced masters. The value is the
 * provider's sp_ref (GUID) — the key runtime matches against — so an admin can never
 * attach a template to a reference that no journey carries. Every provider is listed with
 * its journey count from Dataverse (ss_investorproducts); when that differs from what this
 * environment has synced, the platform count is shown alongside.
 */
export const ServiceProviderPicker: React.FC<Props> = ({ value, onChange, required }) => {
  const [options, setOptions] = useState<ServiceProviderOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    formTemplateService.serviceProviders()
      .then(setOptions)
      .catch((err: any) => toast.error(err.response?.data?.message || 'Failed to load service providers'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <label className="form-label">Service provider {required && <span className="text-danger">*</span>}</label>
      <select
        className="form-select form-select-sm"
        value={value}
        required={required}
        disabled={loading}
        onChange={(e) => onChange(options.find((o) => o.spRef === e.target.value) || null)}
      >
        <option value="">{loading ? 'Loading providers…' : '— choose a service provider —'}</option>
        {options.map((o) => (
          <option key={o.spRef} value={o.spRef}>
            {o.spName}{o.code && o.code !== o.spName ? ` (${o.code})` : ''} · {o.spType}
            {` · ${o.journeyCount} journey${o.journeyCount === 1 ? '' : 's'}`}
            {o.journeyCountSource === 'DATAVERSE' && o.platformJourneyCount !== o.journeyCount
              ? ` (${o.platformJourneyCount} on this platform)` : ''}
          </option>
        ))}
      </select>
      {value && <div className="form-text">Reference: <code>{value}</code></div>}
    </>
  );
};
